import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as path from "node:path";

import {
  CliError,
  EXIT_INPUT,
  EXIT_OK,
  EXIT_USAGE,
  scriptJsonPath,
  sha256Text,
  type CommandOutput,
} from "../../common.js";
import { composeCreativeDraft } from "../../domain/creative-draft/compose.js";
import { requireStructurallyReadableScriptV4 } from "../../domain/script/v4/parse.js";
import {
  assertScriptUnchanged,
  atomicWriteJson,
  semanticScriptHash,
} from "../../infra/script-working-copy.js";
import { strOf, type Dict } from "../../domain/script/shared.js";

function usage(message: string, actual: string[] = []): never {
  throw new CliError(message, {
    exitCode: EXIT_USAGE,
    errorCode: "CREATIVE_DRAFT_USAGE_INVALID",
    hint: "Run `scriptctl authoring compose <draft.json> --script-path <script.json>` and apply the returned plan digest.",
    data: { actual },
  });
}

function readJsonFile(filePath: string, label: string): { path: string; bytes: Buffer; value: unknown } {
  const absolute = path.resolve(filePath);
  let bytes: Buffer;
  try {
    bytes = fs.readFileSync(absolute);
  } catch {
    throw new CliError(`${label} does not exist.`, {
      exitCode: EXIT_INPUT,
      errorCode: "CREATIVE_DRAFT_SOURCE_UNAVAILABLE",
      data: { actual: [absolute] },
    });
  }
  try {
    return { path: absolute, bytes, value: JSON.parse(bytes.toString("utf-8")) };
  } catch (error) {
    throw new CliError(`${label} is not valid JSON.`, {
      exitCode: EXIT_INPUT,
      errorCode: "CREATIVE_DRAFT_JSON_INVALID",
      data: { actual: [absolute, error instanceof Error ? error.message : String(error)] },
    });
  }
}

export function commandComposeCreativeDraft(opts: Dict): [CommandOutput, number] {
  const args = Array.isArray(opts["_args"]) ? opts["_args"] : [];
  const draftArg = typeof args[0] === "string" ? args[0] : "";
  if (!draftArg || draftArg === "-") usage("Creative draft input must be a named JSON file.", [draftArg || "<missing>"]);
  const draft = readJsonFile(draftArg, "The creative draft");
  const scriptPath = scriptJsonPath(opts);
  const currentFile = readJsonFile(scriptPath, "The target Script v4 file");
  const current = requireStructurallyReadableScriptV4(currentFile.value);
  const baseline = semanticScriptHash(current);
  const composed = composeCreativeDraft(current, draft.value);
  const sourceDigest = `sha256:${crypto.createHash("sha256").update(draft.bytes).digest("hex")}`;
  const identity = {
    version: 1,
    op: "compose",
    sourceDigest,
    baseline,
    proposed: semanticScriptHash(composed.script),
    episodeNumber: composed.episodeNumber,
    sceneNumber: composed.sceneNumber,
    sceneId: composed.sceneId,
    actionIds: composed.actionIds,
    joinedPreviousScene: composed.joinedPreviousScene,
    created: composed.created,
  };
  const digest = `sha256:${sha256Text(JSON.stringify(identity))}`;
  const apply = opts["apply"] === true;
  const suppliedPlan = strOf(opts["plan"]).trim();
  if (apply && !suppliedPlan) usage("--apply requires the exact digest returned by the current plan.", ["plan=<missing>"]);
  if (!apply && suppliedPlan) usage("--plan is only valid together with --apply.", [`plan=${suppliedPlan}`]);
  if (apply && suppliedPlan !== digest) {
    throw new CliError("The supplied plan does not match the current draft or script baseline.", {
      exitCode: EXIT_INPUT,
      errorCode: "CREATIVE_DRAFT_PLAN_MISMATCH",
      hint: "Review the newly computed plan, then apply its exact digest.",
      data: { expected: [digest], actual: [suppliedPlan] },
    });
  }
  if (apply) {
    assertScriptUnchanged(scriptPath, baseline);
    atomicWriteJson(scriptPath, composed.script);
  }
  const summary = `${apply ? "Applied" : "Planned"} readable scene draft as episode ${composed.episodeNumber}, scene ${composed.sceneNumber}${composed.joinedPreviousScene ? " (joined previous scene)" : ""}`;
  return [{
    text: [summary, `plan: ${digest}`].join("\n"),
    data: {
      op: "compose",
      applied: apply,
      source_path: draft.path,
      source_digest: sourceDigest,
      script_path: scriptPath,
      baseline,
      plan_digest: digest,
      episode_number: composed.episodeNumber,
      scene_number: composed.sceneNumber,
      scene_id: composed.sceneId,
      action_ids: composed.actionIds,
      joined_previous_scene: composed.joinedPreviousScene,
      created: composed.created,
      validation: composed.validation,
    },
  }, EXIT_OK];
}
